import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors } from "../colors";
import { useApiError } from "../hooks/useApiError";
import { useError } from "../contexts/ErrorContext";
import { useSchoolRequest } from "../hooks/useSchoolRequest";
import { useCaptcha } from "../hooks/useCaptcha";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { SchoolRequestRequestDto } from "../hooks/useSchoolRequest";

const ContactUs: React.FC = () => {
  const navigate = useNavigate();
  const { handleCreateError } = useApiError();
  const { createSchoolRequestMutation } = useSchoolRequest();
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("Request Submitted");
  const [dialogMessage, setDialogMessage] = useState(
    "Your request is submitted we will get back to you soon. Thank you"
  );
  const [errors, setErrors] = useState({
    email: "",
    mobileNo: "",
    pincode: "",
    captcha: "",
  });
  const [captchaValue, setCaptchaValue] = useState("");
  const { captchaUrl, refreshCaptcha, captchaQuery } = useCaptcha(true);

  const [formData, setFormData] = useState<SchoolRequestRequestDto>({
    schoolName: "",
    email: "",
    mobileNo: "",
    addressLine1: "",
    addressLine2: "",
    district: "",
    state: "",
    pincode: "",
    status: "PENDING",
    description: "",
  });

  const handleClose = () => navigate("/");

  const validateForm = () => {
    const newErrors = { email: "", mobileNo: "", pincode: "", captcha: "" };
    let isValid = true;

    const email = formData.email.trim();
    if (!email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    const mobile = formData.mobileNo.trim();
    if (!mobile) {
      newErrors.mobileNo = "Mobile No is required";
      isValid = false;
    } else if (mobile.length !== 10) {
      newErrors.mobileNo = "Mobile number must be exactly 10 digits";
      isValid = false;
    }

    const pin = formData.pincode.trim();
    if (!pin) {
      newErrors.pincode = "Pincode is required";
      isValid = false;
    } else if (pin.length !== 6) {
      newErrors.pincode = "Pincode must be exactly 6 digits";
      isValid = false;
    }

    if (!captchaValue.trim()) {
      newErrors.captcha = "Captcha is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await createSchoolRequestMutation.mutateAsync({
        ...formData,
        description: formData.description?.trim() ? formData.description : "",
        captcha: captchaValue,
      });
      setDialogTitle("Request Submitted");
      setDialogMessage("Your request is submitted we will get back to you soon. Thank you");
      setSuccessDialogOpen(true);
    } catch (err) {
      const status = (err as any)?.response?.status ?? (err as any)?.status;
      const message =
        (err as any)?.response?.data?.message ??
        (err as any)?.response?.data?.error ??
        (err as any)?.message;

      if (typeof message === "string" && message.toLowerCase().includes("captcha")) {
        setErrors((prev) => ({ ...prev, captcha: message || "Invalid captcha" }));
        refreshCaptcha();
        return;
      }

      if (status === 409 && message) {
        setDialogTitle("Request In Progress");
        setDialogMessage(String(message));
        setSuccessDialogOpen(true);
        return;
      }

      handleCreateError(err, "request");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: `linear-gradient(135deg, ${colors.primaryLight} 50%, white 70%)`,
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Card
        sx={{
          maxWidth: 900,
          width: "100%",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          borderRadius: 2,
          mx: 2,
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 1,
            color: "#666",
            transition: "all 0.2s ease",
            "&:hover": {
              color: "#3092e3",
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(48, 146, 227, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ textAlign: "center" }}>
              Contact Us
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              Please fill in the details and we’ll get back to you.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="School Name"
                  value={formData.schoolName}
                  onChange={(e) => setFormData((p) => ({ ...p, schoolName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((p) => ({ ...p, email: value }));
                    if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                  }}
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mobile No"
                  value={formData.mobileNo}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setFormData((p) => ({ ...p, mobileNo: value }));
                    if (errors.mobileNo) setErrors((prev) => ({ ...prev, mobileNo: "" }));
                  }}
                  inputProps={{ maxLength: 10 }}
                  required
                  error={!!errors.mobileNo}
                  helperText={errors.mobileNo}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={formData.pincode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setFormData((p) => ({ ...p, pincode: value }));
                    if (errors.pincode) setErrors((prev) => ({ ...prev, pincode: "" }));
                  }}
                  inputProps={{ maxLength: 6 }}
                  required
                  error={!!errors.pincode}
                  helperText={errors.pincode}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData((p) => ({ ...p, addressLine1: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData((p) => ({ ...p, addressLine2: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="District"
                  value={formData.district}
                  onChange={(e) => setFormData((p) => ({ ...p, district: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => setFormData((p) => ({ ...p, state: e.target.value }))}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                  multiline
                  minRows={4}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <IconButton
                    onClick={() => refreshCaptcha()}
                    disabled={captchaQuery.isFetching}
                    aria-label="Refresh captcha"
                  >
                    <RefreshIcon />
                  </IconButton>
                  <Box
                    component="img"
                    src={captchaUrl ?? ""}
                    alt="Captcha"
                    sx={{
                      height: 48,
                      width: 150,
                      border: "none",
                      bgcolor: "white",
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Enter Captcha"
                    value={captchaValue}
                    onChange={(e) => {
                      setCaptchaValue(e.target.value.slice(0, 5));
                      if (errors.captcha) setErrors((prev) => ({ ...prev, captcha: "" }));
                    }}
                    required
                    error={!!errors.captcha}
                    helperText={errors.captcha}
                    inputProps={{
                      maxLength: 5,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={createSchoolRequestMutation.isLoading}
              sx={{
                mt: 4,
                py: 2,
                fontWeight: "bold",
                fontSize: 16,
                backgroundColor: "#3092e3",
              }}
            >
              {createSchoolRequestMutation.isLoading ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={successDialogOpen}
        fullWidth
        maxWidth="xs"
        onClose={() => {
          setSuccessDialogOpen(false);
          navigate("/");
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" fontWeight={700}>
              {dialogTitle}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setSuccessDialogOpen(false);
                navigate("/");
              }}
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {dialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
              borderRadius: 2,
              textTransform: "none",
              px: 4,
              fontWeight: 700,
            }}
            onClick={() => {
              setSuccessDialogOpen(false);
              navigate("/");
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactUs;

